import {isValid, parseISO} from "date-fns";

/**
 * Format a date without time.
 * Returns defaultValue when no valid date is found.
 */
export function formatDate(
    date: string | null | undefined,
    language: string = "de",
    defaultValue: string | null = null,
): string | null {
    if (!date) return defaultValue;
    const timestamp = Date.parse(date);
    if (Number.isNaN(timestamp)) return defaultValue;
    return new Intl.DateTimeFormat(language, {dateStyle: "medium"}).format(timestamp);
}

/**
 * Format an ISO calendar day (YYYY-MM-DD) as a numeric day (de: TT.MM.JJJJ), read in local time –
 * unlike {@link formatDate}, whose `Date.parse` reads a date-only string as UTC midnight (one day
 * earlier west of UTC). Anything that is not a valid ISO day is returned unchanged.
 */
export function formatIsoDay(isoDay: string, language: string = "de"): string {
    const date = parseISO(isoDay);
    if (!isValid(date)) return isoDay;
    return new Intl.DateTimeFormat(language, {day: "2-digit", month: "2-digit", year: "numeric"}).format(date);
}

/**
 * Format a date with time (HH:MM).
 * Returns defaultValue when no valid date is found.
 */
export function formatDateAndTime(
    date: string | null | undefined,
    language: string = "de",
    defaultValue: string | null = null,
): string | null {
    if (!date) return defaultValue;
    const timestamp = Date.parse(date);
    if (Number.isNaN(timestamp)) return defaultValue;
    return new Intl.DateTimeFormat(language, {dateStyle: "medium", timeStyle: "short"}).format(timestamp);
}

/**
 * Format only the time of day (HH:MM) of a date.
 * Returns defaultValue when no valid date is found.
 */
export function formatTime(
    date: string | null | undefined,
    language: string = "de",
    defaultValue: string | null = null,
): string | null {
    if (!date) return defaultValue;
    const timestamp = Date.parse(date);
    if (Number.isNaN(timestamp)) return defaultValue;
    return new Intl.DateTimeFormat(language, {timeStyle: "short"}).format(timestamp);
}

/**
 * Check if two dates fall on the same (local) calendar day.
 * If either date is empty/invalid, returns false.
 */
export function isSameDay(
    date: string | Date | null | undefined,
    comparisonDate: string | Date | null | undefined = new Date(),
): boolean {
    return compareDates(date, comparisonDate, (d1, d2) => d1.valueOf() === d2.valueOf());
}

/**
 * Check if date is after another provided date (without time).
 * If provided date is empty, always return false.
 */
export function isDateAfterDate(
    date: string | Date | null | undefined,
    comparisonDate: string | Date | null | undefined = new Date(),
): boolean {
    return compareDates(date, comparisonDate, (d1, d2) => d1.valueOf() > d2.valueOf());
}

/**
 * Check if date is after another provided date (without time).
 * If provided date is empty, always return false.
 */
export function isDateBeforeDate(
    date: string | Date | null | undefined,
    comparisonDate: string | Date | null | undefined = new Date(),
): boolean {
    return compareDates(date, comparisonDate, (d1, d2) => d1.valueOf() < d2.valueOf());
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Restlaufzeit bis zu einem Zeitpunkt in angefangenen Tagen (aufgerundet), nie negativ — z. B. für „Löschung in …
 * Tagen" (#19481). 0 heißt: noch heute bzw. schon fällig.
 * @param date Zielzeitpunkt
 * @param now Bezugszeitpunkt (Default: jetzt)
 * @return Tage oder null ohne gültiges Datum
 */
export function daysUntil(date: string | null | undefined, now: Date = new Date()): number | null {
    if (!date) return null;
    const timestamp = Date.parse(date);
    if (Number.isNaN(timestamp)) return null;
    return Math.max(0, Math.ceil((timestamp - now.valueOf()) / DAY_MS));
}

/**
 * Compare dates (without time component).
 * If provided date is empty, always return false.
 */
function compareDates(
    date: string | Date | null | undefined,
    comparisonDate: string | Date | null | undefined,
    fnCompare: (d1: Date, d2: Date) => boolean,
): boolean {
    if (!date || !comparisonDate) return false;
    const dateWithoutTime = convertDateStrToDateWithoutTime(date);
    const comparisonDateWithoutTime = convertDateStrToDateWithoutTime(comparisonDate);
    if (!dateWithoutTime || !comparisonDateWithoutTime) return false;
    return fnCompare(dateWithoutTime, comparisonDateWithoutTime);
}

/**
 * Convert a Date string or Date object to a validated Date object without the time component.
 * Returns either a valid Date, or null.
 */
function convertDateStrToDateWithoutTime(dateInput: string | Date | null | undefined): Date | null {
    if (!dateInput) return null;
    const date = typeof dateInput === "string" ? new Date(dateInput) : new Date(dateInput.valueOf());
    if (Number.isNaN(date.valueOf())) return null;
    date.setHours(0, 0, 0, 0);
    return date;
}
